import AdminPageHeader from "@/components/admin/page-header";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, UserCircle2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminUsers() {
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback();
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", outlet_id: "" });

  const load = useCallback(() => run(async () => {
    const [u, o] = await Promise.all([api.get("/users"), api.get("/outlets")]);
    setUsers(u.data);
    setOutlets(o.data);
  }), [run]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.email || !form.password || !form.outlet_id) return toast.error("Email, password, dan outlet wajib diisi");
    try {
      await api.post("/users", form);
      toast.success("Outlet admin dibuat");
      setOpen(false);
      setForm({ email: "", password: "", name: "", outlet_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User terhapus"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const outletName = (id) => outlets.find((o) => o.id === id)?.name || "-";

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat data" : "Data belum tersedia"} description={loadError} onRetry={loadError ? load : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-users-page">
      {loadError && <PageState kind="error" title="Data belum dapat diperbarui" description={loadError} onRetry={load} />}
      <AdminPageHeader title="Kelola Staf" description={<> Super admin & outlet admin </>} actions={<Button onClick={() => setOpen(true)} className="rounded-full" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-1"/> <span className="hidden sm:inline">Tambah Outlet Admin</span><span className="sm:hidden">Tambah</span>
        </Button>} />

      {users.length === 0 ? <PageState className="surface-card" title="Belum ada staf" description="Tambahkan outlet admin untuk mengelola akses outlet." /> : <div className="surface-card admin-table-scroll overflow-x-auto" tabIndex={0} role="region" aria-label="Daftar staf, geser untuk melihat semua kolom">
        <table className="w-full text-sm">
          <thead className="bg-accent/50 border-b border-border text-left">
            <tr>
              <th className="p-3 font-medium">Email</th>
              <th className="p-3 font-medium hidden sm:table-cell">Nama</th>
              <th className="p-3 font-medium">Role</th>
              <th className="p-3 font-medium">Outlet</th>
              <th className="p-3 font-medium text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.email}`}>
                <td className="p-3 font-medium truncate max-w-[240px]">{u.email}</td>
                <td className="p-3 text-muted-foreground hidden sm:table-cell">{u.name}</td>
                <td className="p-3">
                  {u.platform_role === "super_admin" ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                      <ShieldCheck className="w-3 h-3"/> Super Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-accent text-foreground">
                      <UserCircle2 className="w-3 h-3"/> Outlet Admin
                    </span>
                  )}
                </td>
                <td className="p-3 text-muted-foreground text-xs">{u.platform_role === "super_admin" ? "— All —" : outletName(u.outlet_id)}</td>
                <td className="p-3 text-right">
                  {u.platform_role !== "super_admin" && (
                    <Button variant="ghost" size="sm" aria-label={`Hapus pengguna ${u.name || u.email}`} onClick={() => remove(u.id)} data-testid={`delete-user-${u.email}-btn`}>
                      <Trash2 className="w-4 h-4 text-destructive"/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Outlet Admin</DialogTitle>
            <DialogDescription>Outlet admin hanya dapat mengakses outlet yang di-assign.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label htmlFor="adminusers-field-1">Email</Label><Input id="adminusers-field-1" type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="bg-white" data-testid="user-email-input"/></div>
            <div><Label htmlFor="adminusers-field-2">Nama</Label><Input id="adminusers-field-2" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="bg-white"/></div>
            <div><Label htmlFor="adminusers-field-3">Password</Label><Input id="adminusers-field-3" type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="bg-white" data-testid="user-password-input"/></div>
            <div>
              <Label htmlFor="adminusers-select-4">Outlet</Label>
              <Select value={form.outlet_id} onValueChange={(v)=>setForm({...form,outlet_id:v})}>
                <SelectTrigger id="adminusers-select-4" className="bg-white" data-testid="user-outlet-select"><SelectValue placeholder="Pilih outlet..."/></SelectTrigger>
                <SelectContent className="admin-popup">
                  {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={create} data-testid="save-user-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
